/**
 * AK-423 — карточка поля (leaf-модель, ADR-049).
 *
 * Здесь остаётся ТОЛЬКО то, что справочником не является: поведенческие
 * правила и UI-словари экрана. Сами справочные списки (состояния поля,
 * типы угодий) приходят с сервера — `@/lib/reference/fieldCatalogs`
 * (`useFieldStates` / `useLandCategories`). Копий каталога в коде больше нет:
 * справочник ведёт агроном в БД, а не разработчик в constants.ts.
 */
import type { CodeLabeler } from '@/lib/reference/fieldCatalogs';

/**
 * Какие состояния поля предлагаем под плиткой «Оставить под пар».
 *
 * ЭТО НЕ СПРАВОЧНИК, а продуктовое решение: в reference.field_states 7 строк,
 * но форма посева исторически показывает только виды пара (`fallow_*`) —
 * выводное поле / под мелиорацией задаются не здесь. Список опций тянем из
 * справочника, а этот предикат лишь сужает его до прежнего набора, чтобы
 * рефакторинг источника данных не расширил функционал молча.
 */
export function isFallowKindCode(code?: string | null): boolean {
  return Boolean(code && code.startsWith('fallow_'));
}

/**
 * Состояния поля, которые бывают ТОЛЬКО в рисовой системе
 * (fields.is_rice_system, миграция 102). АМП — агромелиоративное поле: сезон
 * без продуктивной культуры, встречается только на рису.
 *
 * Тоже ПОВЕДЕНЧЕСКОЕ правило, а не содержимое справочника: в
 * reference.field_states нет признака «только для рисовой системы», зеркалим
 * RICE_ONLY_STATES из field_card.py (backend отдаёт 422, если прислать amp на
 * нерисовое поле). Подписи при этом всё равно берём из справочника.
 */
export const RICE_ONLY_STATE_CODES: ReadonlySet<string> = new Set(['amp']);

/** Код состояния поля — из «только рисовых» (АМП)? */
export function isRiceOnlyState(code?: string | null): boolean {
  return Boolean(code && RICE_ONLY_STATE_CODES.has(code));
}

/** Строка справочника состояний поля в том виде, в каком её рисует форма. */
export interface FieldStateOption {
  code: string;
  name: string;
}

/**
 * Что предлагаем в блоке «Вид пара»: обычным полям — только виды пара, полям
 * рисовой системы — плюс АМП. Backend валидирует то же самое (422, если
 * прислать amp на нерисовое поле) — здесь просто не показываем лишнего.
 *
 * Сам список строк приходит из reference.field_states (`useFieldStates`);
 * функция только фильтрует его, порядок справочника сохраняем.
 */
export function fieldStateOptions<T extends FieldStateOption>(
  items: ReadonlyArray<T>,
  isRiceSystem: boolean,
): T[] {
  return items.filter(
    (st) =>
      isFallowKindCode(st.code) || (isRiceSystem && isRiceOnlyState(st.code)),
  );
}

/**
 * Сидерат имеет смысл только для сидерального / занятого пара.
 *
 * Тоже ПОВЕДЕНЧЕСКОЕ правило (когда показывать выбор культуры-сидерата), а не
 * содержимое справочника: в reference.field_states нет признака «нужен
 * сидерат». Появится такой атрибут в каталоге — переносим сюда fetch.
 */
export const SIDERAT_FALLOW_KINDS = new Set(['fallow_sideral', 'fallow_occupied']);

/** Жизненный цикл поля (fields.lifecycle_status + field_card /lifecycle). */
export type LifecycleStatus = 'active' | 'withdrawn' | 'archived';

export const LIFECYCLE_STEPS: ReadonlyArray<{
  code: LifecycleStatus;
  label: string;
}> = [
  { code: 'active', label: 'Активно' },
  { code: 'withdrawn', label: 'Выведено' },
  { code: 'archived', label: 'Архив' },
];

export function lifecycleLabel(status?: string | null): string {
  return LIFECYCLE_STEPS.find((s) => s.code === status)?.label ?? 'Активно';
}

/**
 * Подпись занятия сезона БЕЗ культуры: «Пар» / «Сидеральный пар» / «АМП».
 *
 * Подпись берём из справочника КАК ЕСТЬ: в reference.field_states лежат уже
 * готовые названия («Пар», «Сидеральный пар», «АМП»), достраивать к ним слово
 * «Пар» нельзя — вышло бы «Пар сидеральный пар». Заодно это само собой держит
 * правило «АМП паром не называем» (язык агронома).
 *
 * `stateLabel` — лукап из справочника (`useFieldStateLabel()`). Не передан или
 * справочник ещё грузится → общее «Под паром»: код на экран не течёт.
 */
export function fieldStateLabel(
  code?: string | null,
  stateLabel?: CodeLabeler,
): string {
  if (!code) return 'Под паром';
  return stateLabel?.(code) ?? 'Под паром';
}

/**
 * Этап посева (План → Посеян → Вегетация → Убирают → Убран), подписи и правило
 * «можно ли отметить посеяно» переехали в `@/lib/sowings/stage` — ОДИН источник
 * на карточку поля и на попап карты (AK-434). Здесь их больше нет намеренно:
 * два словаря разъехались и дали founder'у два разных утверждения об одном
 * посеве.
 */

/** Русские подписи типов событий журнала (field_card JournalEvent.type). */
export const JOURNAL_TYPE_LABELS: Record<string, string> = {
  created: 'Создание',
  area: 'Площадь',
  geometry: 'Геометрия',
  rename: 'Имя',
  farm: 'Хозяйство',
  brigade: 'Бригада',
  land_category: 'Угодье',
  // Терминология по эталону founder (2026-07-11): пользователь не видит
  // технических слов «Lifecycle»/«Занятие».
  lifecycle: 'Жизненный цикл',
  occupancy: 'Посев',
  // Пакет 1 «История геометрии»: события зон поля (создана / границы изменены /
  // удалена). Слова «суб-зона» и «leaf» на экран не выносим.
  zone: 'Зоны',
};

/**
 * Очеловечивание текста журнала (эталон founder 2026-07-11).
 *
 * Backend (field_card.py) кладёт в what/after технические строки: «Занятие
 * сезона», а вид пара приходит сырым кодом field_states — «Пар (fallow_black)».
 * Пользователю показываем «Посев» и подпись состояния из справочника. API не
 * трогаем — правим на клиенте.
 *
 * `fallowLabel` — лукап из справочника (`useFieldStateLabel()`). Не передан
 * или справочник ещё грузится → остаётся общее «Пар», код на экран не течёт.
 */
export function humanizeJournalText(
  text?: string | null,
  fallowLabel?: CodeLabeler,
): string | null {
  if (!text) return text ?? null;
  let out = text;
  // «Пар (amp)» → «АМП»: АМП паром не является, backend кладёт его в ту же
  // строку журнала (field_card.py, ветка field_state_id). Подпись — из
  // справочника, пока он не приехал остаётся общее «Пар».
  out = out.replace(/Пар \((amp)\)/gi, (_m, code: string) => {
    return fallowLabel?.(code) ?? 'Пар';
  });
  // «Пар (fallow_black)» → «Пар чёрный» (код вида пара → ru-подпись из справочника).
  out = out.replace(/Пар \((fallow_[a-z]+)\)/gi, (_m, code: string) => {
    return fallowLabel?.(code) ?? 'Пар';
  });
  // Одиночный код вида пара вне скобок — тоже в подпись.
  out = out.replace(/\bfallow_[a-z]+\b/gi, (m) => fallowLabel?.(m) ?? m);
  // «Занятие сезона» / «Занятие» → «Посев» (founder: «занять — фигня»).
  out = out.replace(/Занятие сезона/gi, 'Посев').replace(/Занятие/gi, 'Посев');
  return out;
}

/**
 * Технические причины версий контура («AK-180 fix: ST_Union …», ссылки на
 * тикеты, вызовы PostGIS) прячем под «подробнее» — обычному пользователю это
 * шум. Эвристика: тикет-код (AK-123) или явно машинный маркер.
 */
export function isTechnicalReason(reason?: string | null): boolean {
  if (!reason) return false;
  return /\bAK-\d+\b|ST_[A-Za-z]+|::|geometry|multipolygon|st_union/i.test(reason);
}
